import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LayoutGroup, motion } from 'framer-motion';

type NativeDashboardCard = {
  id: string;
  rowSpan: number;
};

type NativeDashboardSectionGridProps = {
  sectionId: string | number;
  columnCount: number;
  initialColumnsData: NativeDashboardCard[][];
  onColumnsChange: (nextColumns: string[][]) => void;
  renderCard: (cardId: string) => React.ReactNode;
};

const ROW_HEIGHT = 40;
const GRID_GAP_PX = 16;
const MIN_BOTTOM_DROP_ZONE_HEIGHT = 36;
const SWAP_TRIGGER_PERCENT = 0.2;

const serializeColumns = (columns: NativeDashboardCard[][]) =>
  columns.map((column) => column.map((card) => `${card.id}:${card.rowSpan}`).join('|')).join('||');

const cloneColumns = (columns: NativeDashboardCard[][]) =>
  columns.map((column) => column.map((card) => ({ ...card })));

const clearGhostClasses = () => {
  try {
    document.querySelectorAll('.dashboard-native-dragging').forEach((element) => {
      element.classList.remove('dashboard-native-dragging', 'opacity-70', 'border-dashed');
    });
  } catch {}
};

export default function NativeDashboardSectionGrid({
  sectionId,
  columnCount,
  initialColumnsData,
  onColumnsChange,
  renderCard,
}: NativeDashboardSectionGridProps) {
  const [columns, setColumns] = useState<NativeDashboardCard[][]>(() => cloneColumns(initialColumnsData));
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const draggingIdRef = useRef<string | null>(null);
  const pendingSwapRef = useRef<{ targetColIndex: number; targetCardIndex: number } | null>(null);
  const pendingSwapFrameRef = useRef<number | null>(null);
  const columnsRef = useRef(columns);
  const initialSignature = useMemo(() => serializeColumns(initialColumnsData), [initialColumnsData]);
  const dragStartSignature = useRef<string | null>(null);

  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  useEffect(() => {
    if (draggingId) return;
    if (serializeColumns(columnsRef.current) === initialSignature) return;

    const nextColumns = cloneColumns(initialColumnsData);
    columnsRef.current = nextColumns;
    setColumns(nextColumns);
  }, [draggingId, initialSignature, initialColumnsData]);

  useEffect(() => () => clearGhostClasses(), []);

  const findSourceLocation = (cardId: string) => {
    const currentColumns = columnsRef.current;
    let sourceColIndex = -1;
    let sourceCardIndex = -1;

    for (let columnIndex = 0; columnIndex < currentColumns.length; columnIndex += 1) {
      const cardIndex = currentColumns[columnIndex].findIndex((card) => card.id === cardId);
      if (cardIndex !== -1) {
        sourceColIndex = columnIndex;
        sourceCardIndex = cardIndex;
        break;
      }
    }

    return { sourceColIndex, sourceCardIndex };
  };

  const performSwap = (
    targetColIndex: number,
    targetCardIndex: number,
    sourceColIndex: number,
    sourceCardIndex: number,
  ) => {
    if (sourceColIndex === -1 || sourceCardIndex === -1) return;

    setColumns((prev) => {
      const nextColumns = prev.map((column) => [...column]);
      const draggedCard = nextColumns[sourceColIndex][sourceCardIndex];
      if (!draggedCard) return prev;

      nextColumns[sourceColIndex].splice(sourceCardIndex, 1);

      const adjustedTargetIndex =
        sourceColIndex === targetColIndex && sourceCardIndex < targetCardIndex
          ? targetCardIndex - 1
          : targetCardIndex;

      const normalizedTargetIndex = Math.max(0, Math.min(adjustedTargetIndex, nextColumns[targetColIndex].length));
      nextColumns[targetColIndex].splice(normalizedTargetIndex, 0, draggedCard);
      columnsRef.current = nextColumns;
      return nextColumns;
    });
  };

  const getInsertionIndexForPointer = (targetColIndex: number, pointerY: number) => {
    const currentColumns = columnsRef.current;
    const targetColumn = currentColumns[targetColIndex] ?? [];
    const activeDraggingId = draggingIdRef.current;
    if (!activeDraggingId) return targetColumn.length;

    const activeLocation = findSourceLocation(activeDraggingId);
    const isSameColumn = activeLocation.sourceColIndex === targetColIndex;

    let currentY = 0;
    let insertionIndex = 0;

    for (let cardIndex = 0; cardIndex < targetColumn.length; cardIndex += 1) {
      const card = targetColumn[cardIndex];
      const cardHeight = card.rowSpan * ROW_HEIGHT + Math.max(0, card.rowSpan - 1) * GRID_GAP_PX;

      const top = currentY;
      const bottom = currentY + cardHeight;
      currentY += cardHeight + GRID_GAP_PX;

      if (card.id === activeDraggingId) {
        // IMPORTANT: Accumulate height even for the dragged item, so the layout
        // dimensions perfectly mirror what the pointer coordinates represent, 
        // avoiding infinite swap oscillations!
        continue;
      }

      let triggerY = top + cardHeight / 2;
      if (isSameColumn) {
        if (activeLocation.sourceCardIndex < cardIndex) {
          triggerY = top + cardHeight * SWAP_TRIGGER_PERCENT;
          if (pointerY >= triggerY) {
            insertionIndex = cardIndex + 1;
            continue;
          }

          return insertionIndex;
        } else if (activeLocation.sourceCardIndex > cardIndex) {
          triggerY = bottom - cardHeight * SWAP_TRIGGER_PERCENT;
          if (pointerY > triggerY) {
            insertionIndex = cardIndex + 1;
            continue;
          }

          return cardIndex;
        }
      }

      if (pointerY < triggerY) {
        return cardIndex;
      }

      insertionIndex = cardIndex + 1;
    }

    return insertionIndex;
  };

  const flushPendingSwap = () => {
    pendingSwapFrameRef.current = null;

    const pendingSwap = pendingSwapRef.current;
    const activeDraggingId = draggingIdRef.current;
    if (!pendingSwap || !activeDraggingId) return;

    const { sourceColIndex, sourceCardIndex } = findSourceLocation(activeDraggingId);
    if (sourceColIndex === -1 || (sourceColIndex === pendingSwap.targetColIndex && sourceCardIndex === pendingSwap.targetCardIndex)) {
      return;
    }

    performSwap(
      pendingSwap.targetColIndex,
      pendingSwap.targetCardIndex,
      sourceColIndex,
      sourceCardIndex,
    );
  };

  const queueSwap = (targetColIndex: number, targetCardIndex: number) => {
    pendingSwapRef.current = { targetColIndex, targetCardIndex };

    if (pendingSwapFrameRef.current !== null) return;
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      flushPendingSwap();
      return;
    }

    pendingSwapFrameRef.current = window.requestAnimationFrame(() => {
      flushPendingSwap();
    });
  };

  const finishDrag = () => {
    if (pendingSwapFrameRef.current !== null && typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(pendingSwapFrameRef.current);
    }
    pendingSwapFrameRef.current = null;
    pendingSwapRef.current = null;
    draggingIdRef.current = null;
    setDraggingId(null);

    const currentSignature = serializeColumns(columnsRef.current);
    if (dragStartSignature.current && currentSignature !== dragStartSignature.current) {
      onColumnsChange(columnsRef.current.map((column) => column.map((card) => card.id)));
    }
    dragStartSignature.current = null;

    clearGhostClasses();
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, id: string) => {
    dragStartSignature.current = serializeColumns(columnsRef.current);
    draggingIdRef.current = id;
    setDraggingId(id);
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';

    setTimeout(() => {
      const el = document.getElementById(`card-${id}`);
      if (el) el.classList.add('dashboard-native-dragging', 'opacity-70', 'border-dashed');
    }, 0);
  };

  const handleColumnDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    targetColIndex: number,
  ) => {
    // If the drag originated from outside this section grid, do not stop propagation.
    // This allows DashboardPage to handle cross-section drops.
    if (!draggingIdRef.current) return;

    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';

    const { sourceColIndex, sourceCardIndex } = findSourceLocation(draggingIdRef.current);
    if (sourceColIndex === -1) return;

    const columnRect = e.currentTarget.getBoundingClientRect();
    const pointerY = e.clientY - columnRect.top;

    const insertionIndex = getInsertionIndexForPointer(targetColIndex, pointerY);
    if (sourceColIndex === targetColIndex && sourceCardIndex === insertionIndex) return;

    queueSwap(targetColIndex, insertionIndex);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    // If not dragging an internal item, let the drop event bubble up to the DashboardPage
    if (!draggingIdRef.current) return;
    
    e.preventDefault();
    e.stopPropagation();
    finishDrag();
  };

  const handleDragEnd = () => {
    finishDrag();
  };

  return (
    <LayoutGroup id={`dashboard-section-${String(sectionId)}`}>
      <div
        className="grid items-start gap-4"
        style={{ gridTemplateColumns: `repeat(${Math.max(1, columnCount)}, minmax(0, 1fr))` }}
      >
        {columns.map((column, columnIndex) => (
          <div
            key={`section-${String(sectionId)}-col-${columnIndex}`}
            className="flex min-h-[220px] flex-col gap-4"
            data-dashboard-column={columnIndex}
            onDragOver={(e) => handleColumnDragOver(e, columnIndex)}
          >
            {column.map((card) => {
              const pixelHeight = card.rowSpan * ROW_HEIGHT + Math.max(0, card.rowSpan - 1) * GRID_GAP_PX;
              return (
                <motion.div
                  key={card.id}
                  id={`card-${card.id}`}
                  layout
                  layoutId={`dashboard-card-${card.id}`}
                  transition={{ type: 'spring', stiffness: 280, damping: 28, mass: 1 }}
                  draggable
                  onDragStartCapture={(e) => handleDragStart(e, card.id)}
                  onDrop={handleDrop}
                  onDragEndCapture={handleDragEnd}
                  className={`relative rounded-2xl border border-transparent transition-opacity ${draggingId === card.id ? 'z-20 opacity-70' : ''}`}
                  style={{ height: `${pixelHeight}px`, minHeight: `${pixelHeight}px` }}
                >
                  {renderCard(card.id)}
                </motion.div>
              );
            })}
            <div
              className="flex-1 rounded-2xl border border-dashed border-transparent transition-colors hover:border-blue-500/30"
              style={{ minHeight: `${MIN_BOTTOM_DROP_ZONE_HEIGHT}px` }}
            />
          </div>
        ))}
      </div>
    </LayoutGroup>
  );
}
